import React from 'react';
import NdoeDataCard from './NdoeDataCard';

/*
  CARTE MOBILE DÉDUITE DES COLONNES (audit UI 16/07, lot 3).

  POURQUOI CE MODULE EXISTE
  Le design system dit depuis toujours « jamais de table en scroll horizontal
  sur mobile → liste de cartes » (README.md). Compté sur origin/main au
  16/07/2026 : 75 fichiers rendent encore un <Table> antd brut, contre 38 qui
  utilisent NdoeResponsiveTable — soit 2 contre 1. Les 75 n'ont aucun rendu
  mobile : sur un téléphone, leurs colonnes s'écrasent ou débordent.

  `renderCard` était OBLIGATOIRE : migrer un tableau imposait d'écrire une carte
  à la main, un vrai travail de conception. Ce n'est pas une explication
  suffisante — 39 fichiers en ont écrit une — mais c'est un coût fixe payé à
  chaque migration, et il n'y a aucune raison de le payer quand les colonnes
  disent déjà quoi afficher.

  Ce module retire ce coût : la carte se DÉDUIT des colonnes déjà écrites.
  Migrer devient un changement d'une ligne quand les colonnes s'y prêtent.
  Une carte sur mesure reste possible, et MEILLEURE quand l'écran le mérite :
  `renderCard` garde la priorité. C'est le bon choix dès qu'un render est taillé
  pour une largeur de colonne fixe (il tronquerait dans une carte plus large),
  ou qu'une page filtre ses colonnes par permission — voir JournalStock.

  CE QU'ON DÉDUIT, ET COMMENT
  - colonne d'index (« # », « No », largeur ~3 %) : ignorée. Un numéro de ligne
    n'a aucun sens dans une carte, qui n'est plus dans un tableau.
  - 1re colonne porteuse de sens : devient le TITRE de la carte.
  - colonne de statut (« Statut », « État »…) : va dans l'emplacement `badge`,
    en haut à droite. C'est le champ qu'on lit en premier ; dans un tableau
    large il se retrouve souvent en fin de ligne, hors écran.
  - colonne d'actions (key/dataIndex « action(s) », ou sans titre) : passe dans
    le pied de carte, là où le pouce l'attend.
  - le reste : autant de métriques « libellé / valeur ». Sans plafond : une
    colonne qu'on cache sans le dire est une donnée perdue, pas une carte plus
    propre. Une carte trop longue se corrige avec un `renderCard` sur mesure —
    un choix explicite, pas un silence.
  On appelle `render` de chaque colonne avec (valeur, enregistrement, index) —
  la signature d'antd — pour que les Tag, badges et montants formatés
  apparaissent dans la carte EXACTEMENT comme dans le tableau. Aucune donnée
  n'est reformatée ici : on réutilise le rendu que la page a déjà décidé.
*/

const CLES_INDEX = new Set(['#', 'no', 'n°', 'index', 'id']);
const CLES_ACTION = new Set(['action', 'actions', 'operation', 'operations']);
const CLES_STATUT = new Set(['statut', 'status', 'état', 'etat']);

const texteDe = (v) => (typeof v === 'string' || typeof v === 'number' ? String(v) : '');

/**
 * Une colonne « numéro de ligne » ?
 * La condition `!col.dataIndex` porte sur les DEUX branches, et c'est essentiel :
 * une colonne titrée « N° » AVEC un dataIndex n'est pas un numéro de ligne, c'est
 * une donnée (ClientView.jsx:61 → le numéro du client). La jeter la ferait
 * disparaître du mobile sans un mot.
 */
const estIndex = (col) => {
  const t = texteDe(col.title).trim().toLowerCase();
  const k = String(col.key || col.dataIndex || '').toLowerCase();
  return (CLES_INDEX.has(t) || CLES_INDEX.has(k)) && !col.dataIndex;
};

const estAction = (col) => {
  const k = String(col.key || col.dataIndex || '').toLowerCase();
  const t = texteDe(col.title).trim().toLowerCase();
  return CLES_ACTION.has(k) || CLES_ACTION.has(t) || (!col.title && !!col.render);
};

const estStatut = (col) => {
  const k = String(col.key || col.dataIndex || '').toLowerCase();
  const t = texteDe(col.title).trim().toLowerCase();
  return CLES_STATUT.has(k) || CLES_STATUT.has(t);
};

/**
 * Lit la donnée d'une colonne. `dataIndex` peut être un chemin imbriqué sous
 * forme de tableau (['user','nom']) : `record[['user','nom']]` chercherait la
 * clé littérale « user,nom » et ne renverrait jamais rien.
 */
const valeurBrute = (col, record) => {
  const di = col.dataIndex;
  if (di === undefined || di === null) return undefined;
  if (Array.isArray(di)) return di.reduce((o, k) => (o == null ? undefined : o[k]), record);
  return record?.[di];
};

/** Valeur affichable d'une cellule : on passe par le `render` de la colonne. */
const cellule = (col, record, index) => {
  const brut = valeurBrute(col, record);
  if (typeof col.render !== 'function') return brut ?? '—';

  let sortie;
  try {
    sortie = col.render(brut, record, index);
  } catch (e) {
    // Un render qui suppose un contexte de tableau (ex. state de tri) ne doit
    // jamais faire tomber la liste mobile : on retombe sur la valeur brute.
    return brut ?? '—';
  }

  // Format de fusion de cellules antd : { children, props: { colSpan, rowSpan } }.
  // C'est un objet nu, pas un élément React — le rendre ferait planter React
  // (« Objects are not valid as a React child ») APRÈS le try/catch, donc hors
  // de sa portée : l'error boundary tuerait la page. On ne garde que children.
  if (sortie && typeof sortie === 'object' && !React.isValidElement(sortie) && 'children' in sortie) {
    return sortie.children ?? '—';
  }
  return sortie;
};

/**
 * Fabrique un renderCard à partir des colonnes d'un tableau.
 * @param {Array}  columns  colonnes AntD
 * @param {object} opts
 *   - onClick(record)  : rend la carte cliquable (drill-down)
 *   - titreCol         : `key` de la colonne à utiliser comme titre, quand la
 *                        1re n'est pas la plus parlante. Cette colonne est
 *                        CONSOMMÉE par le titre — pas de doublon.
 *   - titreDe(record)  : titre entièrement libre. Ne consomme aucune colonne :
 *                        toutes restent en métriques, sinon la 1re donnée
 *                        disparaîtrait sans un mot. À n'utiliser que pour un
 *                        titre qui ne correspond à aucune colonne ; sinon
 *                        `titreCol` évite d'afficher deux fois la même valeur.
 */
export function carteDepuisColonnes(columns = [], opts = {}) {
  const { onClick, titreDe, titreCol } = opts;
  const utiles = columns.filter((c) => c && !estIndex(c));
  const actions = utiles.filter(estAction);
  const reste = utiles.filter((c) => !estAction(c));
  const colStatut = reste.find(estStatut);
  const donnees = reste.filter((c) => c !== colStatut);

  const parCle = titreCol
    ? donnees.find((c) => String(c.key || c.dataIndex) === String(titreCol))
    : null;
  const colTitre = titreDe ? null : (parCle || donnees[0]);
  const colMetriques = donnees.filter((c) => c !== colTitre);

  return function renderCarteAuto(record, index) {
    const metrics = colMetriques.map((col) => ({
      label: texteDe(col.title) || '',
      value: cellule(col, record, index),
    }));

    const titre = titreDe ? titreDe(record) : (colTitre ? cellule(colTitre, record, index) : null);


    return (
      <NdoeDataCard
        title={titre}
        badge={colStatut ? cellule(colStatut, record, index) : undefined}
        metrics={metrics}
        onClick={onClick ? () => onClick(record) : undefined}
        actions={actions.length
          ? actions.map((col, i) => <span key={col.key || i}>{cellule(col, record, index)}</span>)
          : undefined}
      />
    );
  };
}

export default carteDepuisColonnes;
